import { applyAuthoritativeSelectedUpdates, normalizeSelectedRecoveryIds, recoverSelectedOddsCoalesced, selectedPatchFreshness } from '../_helpers/sportsnewSelectedRecovery';
import { applyOddPatchToFixtureList, isOddLocked, resolveOddFlash } from '../_helpers/sportsnewListPatch';
import { applySportsnewOddPatchToDetailMarkets, hasSportsnewCommandEvidence, reconcileSportsnewSelectedReaderOdd, recordSportsnewCommandOdd } from '../_helpers/sportsnewDetailRealtime';
import { markWsPatchReceived, measureWsPatchToRender } from '../_helpers/sportsnewPerf';
import { parseSportsnewOddStateId, sportsnewOddStateIdOf } from '../_helpers/sportsnewOddStateId';
import { sportsnewSelectedCartPatch } from '../_helpers/sportsnewSelectedCartPatch';
import { useCallback } from 'react';

// Owns selected realtime within the existing split context.
// Provider refs retain account/list/detail authority; callers receive stable actions.
export const useSportsnewSelectedRealtime = ({
  frameDeliveryRef, SportsnewService, acceptDisplayPatch, commitDetailMarkets, commitFixtures,
  detailCacheRef, detailRealtimeLedgerRef, displayScope, failCloseCartSelectionsForFixtures,
  failCloseQuarantinedFixtures, flashOdds, isSelectionInAnyCart, membershipFailClosedFixturesRef,
  selectedAuthoritativeRecoveryOrdinalRef, selectedFreshnessRef, selectedRecoveryInFlightRef, selectedSequenceRef,
  selectionsRef, sendAck, socketGenerationRef, socketRef,
  updateOddsInCart,
}) => {
  const handleSelectedPatch = useCallback(({ sequence, emittedAt, patch }, { continuityAdmitted = false } = {}) => {
      markWsPatchReceived('selected', sequence);
      if (!continuityAdmitted && Number.isSafeInteger(sequence) && Number.isSafeInteger(selectedSequenceRef.current)
        && sequence <= selectedSequenceRef.current) {
        sendAck(patch?.room || 'sportsnew:selected', sequence, emittedAt);
        return;
      }
      if (!continuityAdmitted) selectedSequenceRef.current = sequence;

      const selectedOddStateId = parseSportsnewOddStateId(patch?.oddStateId);
      const incomingFreshness = selectedOddStateId != null
        ? selectedPatchFreshness(patch, sequence)
        : null;
      // Socket sequence is fenced above. A REST/source clock cannot reject a
      // newer server lock; cross-domain unlocks use the shared display verdict.
      const preserveExisting = false;

      const displayPatch = selectedOddStateId != null && !preserveExisting
        ? acceptDisplayPatch(patch, 'selected', {
          room: 'selected', connection: socketGenerationRef.current, sequence,
        }) : null;
      if (selectedOddStateId != null && !preserveExisting) {
        selectedFreshnessRef.current.set(
          selectedOddStateId,
          { ...incomingFreshness, connection: socketGenerationRef.current },
        );
        // Cart, detail and list must consume the same display verdict. In
        // particular a held cross-room unlock must not reach only the cart.
        patch = displayPatch;
        const cartSel = selectionsRef.current.find(
          selection => sportsnewOddStateIdOf(selection) === selectedOddStateId,
        );
        const nextOdds = patch.odds ?? patch.price ?? patch.priceDecimal ?? null;
        const hasLockField =
          patch.bettable !== undefined ||
          patch.locked !== undefined ||
          patch.unavailable !== undefined ||
          patch.is_suspended !== undefined ||
          patch.suspended !== undefined ||
          patch.state !== undefined ||
          patch.displayState !== undefined;

        // A lock-only patch (bettable/suspended/locked/state with no price at
        // all) must still update the cart/betslip and the pick's disabled/
        // locked label — previously this whole block only ran when a price was
        // present, so a pure lock/unlock patch was silently dropped.
        if (nextOdds != null || hasLockField) {
          // Compare the value the user actually sees (two decimals), while
          // preserving same-price lock/unlock flashes.
          const prevOdds = patch.previousOdds ?? (cartSel ? Number(cartSel.odds) : null);
          const selectedPatchLocked = hasLockField ? isOddLocked(patch) : cartSel?.status === 'locked';
          const flashDirection = resolveOddFlash(
            {
              priceDecimal: prevOdds,
              bettable: cartSel?.status !== 'locked',
            },
            {
              ...patch,
              priceDecimal: nextOdds ?? prevOdds,
              ...(hasLockField ? { bettable: !selectedPatchLocked } : {}),
            },
          );
          if (flashDirection) flashOdds(patch.oddStateId, flashDirection);
          // Update the cart's own displayed price/status — this also drives its
          // own flash indicator (FigmaBetCart renders `oddsChanges` independently
          // of the oddsFlash map above, which only covers list/detail cells).
          // newOdds may be null here (lock-only patch) — updateOddsInCart already
          // treats a non-finite newOdds as "no price change", so status/lock
          // fields still apply correctly.
          updateOddsInCart?.(
            selectedOddStateId,
            nextOdds,
            sportsnewSelectedCartPatch(patch),
          );
          const cartFixtureId = cartSel?.match_id
            ?? cartSel?.matchId
            ?? cartSel?.fixtureId
            ?? cartSel?.fixture_id;
          if (
            cartFixtureId != null
            && membershipFailClosedFixturesRef.current.has(String(cartFixtureId))
          ) {
            failCloseCartSelectionsForFixtures([cartFixtureId]);
          }
          // Also patch the DETAIL PANEL's own markets copy if this odd happens to
          // be shown there. oddsFlash is a global oddStateId-keyed map, so the
          // flash above already lights up the detail-panel cell too (it reads
          // oddsFlash with the same id) — but that cell's PRICE/lock fields come
          // from detailMarkets, which this event never touched before, leaving
          // it stuck on the old values even while it visibly flashed.
          commitDetailMarkets((prev) => applySportsnewOddPatchToDetailMarkets(prev, displayPatch));
          detailCacheRef.current.forEach((cached, cacheKey) => {
            const nextMarkets = applySportsnewOddPatchToDetailMarkets(cached?.markets, displayPatch);
            if (nextMarkets !== cached?.markets) {
              detailCacheRef.current.set(cacheKey, { ...cached, markets: nextMarkets });
            }
          });
        }
      }

      // Update odds in fixture list
      if (selectedOddStateId != null && !preserveExisting) {
        commitFixtures((prev) => failCloseQuarantinedFixtures(
          applyOddPatchToFixtureList(prev, displayPatch),
        ));
      }

      sendAck(
        `sportsnew:selected:${socketRef.current?.id || ''}`,
        sequence,
        emittedAt
      );
      measureWsPatchToRender('selected', sequence);
    }, [
    selectedSequenceRef, acceptDisplayPatch, socketGenerationRef, sendAck, socketRef,
    selectedFreshnessRef, selectionsRef, flashOdds, updateOddsInCart,
    membershipFailClosedFixturesRef, commitDetailMarkets, detailCacheRef,
    failCloseCartSelectionsForFixtures, commitFixtures, failCloseQuarantinedFixtures,
  ]);

  const applySelectedRecoveryUpdate = useCallback((update, options = {}) => {
    frameDeliveryRef?.current?.flush();
      const oddStateId = update.oddStateId;
      const displayPatch = update.authority === 'preview_rejection'
        ? recordSportsnewCommandOdd(detailRealtimeLedgerRef.current, update)
        : reconcileSportsnewSelectedReaderOdd(detailRealtimeLedgerRef.current, update);
      const priceDecimal = displayPatch.priceDecimal ?? null;
      const patch = sportsnewSelectedCartPatch(displayPatch);
      const selection = selectionsRef.current.find(
        row => sportsnewOddStateIdOf(row) === oddStateId,
      );
      const acceptedPrice = Number(selection?.odds);
      const nextPrice = priceDecimal == null ? null : Number(priceDecimal);
      const priceChanged = Number.isFinite(nextPrice)
        && (!Number.isFinite(acceptedPrice) || Math.abs(nextPrice - acceptedPrice) > 0.001);
      const lifecycleNeedsAcceptance = patch?.unavailable === true
        || patch?.bettable === false
        || patch?.locked === true
        || patch?.is_suspended === true;
      const requiresAcceptance = update?.requiresAcceptance === true
        || priceChanged
        || lifecycleNeedsAcceptance;
      updateOddsInCart?.(oddStateId, priceDecimal, patch);
      commitFixtures(rows => failCloseQuarantinedFixtures(applyOddPatchToFixtureList(rows, displayPatch)));
      commitDetailMarkets(rows => applySportsnewOddPatchToDetailMarkets(rows, displayPatch));
      detailCacheRef.current.forEach((cached, key) => {
        const markets = applySportsnewOddPatchToDetailMarkets(cached.markets, displayPatch);
        if (markets !== cached.markets) detailCacheRef.current.set(key, { ...cached, markets });
      });
      if (options?.preserveExistingFreshness !== true && (update.authority === 'preview_rejection'
        || !hasSportsnewCommandEvidence(detailRealtimeLedgerRef.current, oddStateId))) {
        selectedFreshnessRef.current.set(
          oddStateId,
          selectedPatchFreshness(update, null),
        );
      }
      const selectionFixtureId = selection?.match_id
        ?? selection?.matchId
        ?? selection?.fixtureId
        ?? selection?.fixture_id;
      if (
        selectionFixtureId != null
        && membershipFailClosedFixturesRef.current.has(String(selectionFixtureId))
      ) {
        failCloseCartSelectionsForFixtures([selectionFixtureId]);
      }
      return { requiresAcceptance };
    }, [
    detailRealtimeLedgerRef, selectionsRef, updateOddsInCart, commitFixtures, commitDetailMarkets,
    detailCacheRef, membershipFailClosedFixturesRef, failCloseQuarantinedFixtures,
    selectedFreshnessRef, failCloseCartSelectionsForFixtures,
  ]);

  const recoverSelectedOddsForCart = useCallback((
      submittedSelections,
      requestedScope = displayScope,
      options = {},
    ) => {
    frameDeliveryRef?.current?.flush();
      const requestedIds = new Set(normalizeSelectedRecoveryIds(submittedSelections));
      const authoritativeUpdates = options?.authoritativeUpdates;

      if (options.isCancelled?.()) {
        return Promise.resolve({
          applied: false,
          recovered: false,
          reason: 'cancelled',
          oddStateIds: [...requestedIds],
          authoritativeAppliedOddStateIds: [],
          authoritativeSkippedNewerOddStateIds: [],
          authoritativeSkippedInactiveOddStateIds: [],
          authoritativeActionableCount: 0,
        });
      }
      if (requestedIds.size === 0) {
        return Promise.resolve({
          recovered: false,
          reason: 'no_selected_odds',
          authoritativeAppliedOddStateIds: [],
          authoritativeSkippedNewerOddStateIds: [],
          authoritativeSkippedInactiveOddStateIds: [],
          authoritativeActionableCount: 0,
        });
      }
      if (![...requestedIds].some(isSelectionInAnyCart)) {
        return Promise.resolve({
          recovered: true,
          reason: 'selections_already_removed',
          authoritativeAppliedOddStateIds: [],
          authoritativeSkippedNewerOddStateIds: [],
          authoritativeSkippedInactiveOddStateIds: [...requestedIds],
          authoritativeActionableCount: 0,
        });
      }

      const authoritativeRecoveryOrdinal = Array.isArray(authoritativeUpdates)
        && authoritativeUpdates.length > 0
        ? ++selectedAuthoritativeRecoveryOrdinalRef.current
        : null;
      const orderedAuthoritativeUpdates = authoritativeRecoveryOrdinal != null
        ? authoritativeUpdates.map(update => ({
          ...update,
          authorityOrdinal: authoritativeRecoveryOrdinal,
        }))
        : [];
      const authoritative = applyAuthoritativeSelectedUpdates({
        authoritativeUpdates: orderedAuthoritativeUpdates,
        requestedValues: submittedSelections,
        freshnessById: selectedFreshnessRef.current,
        isSelectionActive: isSelectionInAnyCart,
        applyUpdate: applySelectedRecoveryUpdate,
      });

      return recoverSelectedOddsCoalesced({
        inFlightRequests: selectedRecoveryInFlightRef.current,
        submittedSelections,
        displayScope: requestedScope,
        recoverSelected: SportsnewService?.recoverSelected ? (...args) => (
        SportsnewService.recoverSelected(...args).then(result => {
          frameDeliveryRef?.current?.flush();
          return result;
        })
      ) : undefined,
        wsFreshnessById: selectedFreshnessRef.current,
        recoveryOwnerKey: options.recoveryOwnerKey,
        isSelectionActive: isSelectionInAnyCart,
        isCancelled: options.isCancelled,
        applyUpdate: applySelectedRecoveryUpdate,
      }).then((recovery) => {
        if (options.isCancelled?.()) {
          return {
            ...recovery,
            applied: false,
            recovered: false,
            reason: 'cancelled',
          };
        }
        const selectionsAlreadyRemoved = ![...requestedIds].some(isSelectionInAnyCart);
        return {
          ...recovery,
          authoritativeAppliedOddStateIds: authoritative.appliedOddStateIds,
          authoritativeSkippedNewerOddStateIds: authoritative.skippedNewerOddStateIds,
          authoritativeSkippedInactiveOddStateIds: authoritative.skippedInactiveOddStateIds,
          authoritativeActionableCount: authoritative.actionableCount,
          recovered: selectionsAlreadyRemoved
            || authoritative.actionableCount > 0
            || authoritative.appliedOddStateIds.length > 0
            || authoritative.skippedNewerOddStateIds.length > 0
            || (recovery?.actionableAppliedOddStateIds?.length ?? 0) > 0
            || (
              (!Array.isArray(authoritativeUpdates) || authoritativeUpdates.length === 0)
              && recovery?.applied === true
            ),
        };
      });
    }, [
    SportsnewService?.recoverSelected, applySelectedRecoveryUpdate, displayScope,
    isSelectionInAnyCart, selectedAuthoritativeRecoveryOrdinalRef, selectedFreshnessRef,
    selectedRecoveryInFlightRef,
  ]);

  return { handleSelectedPatch, recoverSelectedOddsForCart };
};
